// Thin wrapper around the js-aruco2 library
// (https://github.com/damianofalcioni/js-aruco2) exposing an AprilTag detector.
//
// We use the APRILTAG_36h11 dictionary, which matches AprilTags printed in the
// tag36h11 family. The detected marker `id` corresponds directly to the numeric
// AprilTag id (e.g. tagValue "14" => marker id 14).

import type { JsArucoNamespace } from 'js-aruco2';
import type { AprilTag } from '../ts/AprilTag';

// Process at most one frame per this interval to keep CPU usage reasonable on
// mobile devices while still feeling responsive.
const DEFAULT_DETECTION_INTERVAL_MS = 120;
// Require the same forklift on this many consecutive processed frames before
// accepting it, to avoid false positives selecting the wrong forklift.
const DEFAULT_REQUIRED_CONSECUTIVE_MATCHES = 2;
// Cap the processing resolution for performance; detection quality is retained.
const DEFAULT_MAX_PROCESSING_WIDTH = 640;

export interface AprilTagMarker {
    id: number;
    corners: { x: number; y: number }[];
    hammingDistance: number;
}

export interface AprilTagDetector {
    detect(
        image: ImageData | { width: number; height: number; data: Uint8ClampedArray }
    ): AprilTagMarker[];
}

const APRILTAG_DICTIONARY = 'APRILTAG_36h11';

let loadPromise: Promise<JsArucoNamespace> | null = null;

/**
 * Loads the js-aruco2 detector and the tag36h11 dictionary on demand (once).
 * The dictionary module registers itself onto `AR.DICTIONARIES` as a side
 * effect, so it must be imported alongside the core library.
 */
function loadAprilTag(): Promise<JsArucoNamespace> {
    if (loadPromise) {
        return loadPromise;
    }
    loadPromise = (async () => {
        const [{ AR }] = await Promise.all([
            import('js-aruco2'),
            import('js-aruco2/src/dictionaries/apriltag_36h11.js'),
        ]);
        if (!AR || typeof AR.Detector !== 'function') {
            throw new Error('AprilTag detector failed to initialize');
        }
        return AR;
    })();
    // Allow retrying if loading failed.
    loadPromise.catch(() => {
        loadPromise = null;
    });
    return loadPromise;
}

/**
 * Creates a detector configured for the AprilTag tag36h11 family.
 *
 * @param maxHammingDistance Optional stricter error tolerance. Lower values
 *   reduce false positives at the cost of missing markers in low-resolution
 *   frames. When omitted, the dictionary's default tolerance is used.
 */
export async function createForkliftTagDetector(
    maxHammingDistance?: number
): Promise<AprilTagDetector> {
    const AR = await loadAprilTag();
    return new AR.Detector({
        dictionaryName: APRILTAG_DICTIONARY,
        ...(maxHammingDistance !== undefined ? { maxHammingDistance } : {}),
    });
}

export interface ForkliftScanSessionOptions {
    /** Live video element already playing a camera stream. */
    video: HTMLVideoElement;
    /** Forklifts the operator is allowed to select, matched by `tagValue`. */
    forkliftList: AprilTag[];
    /** Called once a forklift tag is confidently detected. */
    onDetected: (forklift: AprilTag) => void;
    /**
     * Called when the "a tag is visible but not one of your forklifts" state
     * changes, so the UI can surface guidance. Only fires on change.
     */
    onUnrecognizedChange?: (unrecognized: boolean) => void;
    detectionIntervalMs?: number;
    requiredConsecutiveMatches?: number;
    maxProcessingWidth?: number;
    maxHammingDistance?: number;
}

export interface ForkliftScanSession {
    /** Stops the detection loop. Safe to call multiple times. */
    stop(): void;
}

function findMatchingForklift(
    markers: AprilTagMarker[],
    forkliftList: AprilTag[]
): AprilTag | null {
    for (const marker of markers) {
        const match = forkliftList.find(
            (f) => parseInt(f.tagValue, 10) === marker.id
        );
        if (match) {
            return match;
        }
    }
    return null;
}

/**
 * Runs the full forklift-tag detection pipeline against a playing `<video>`:
 * throttling, downscaling, canvas readback, tag decoding, matching against the
 * allowed forklift list, and consecutive-frame confirmation. The camera stream
 * itself is owned by the caller; this only manages the detection loop.
 */
export async function startForkliftScanSession(
    options: ForkliftScanSessionOptions
): Promise<ForkliftScanSession> {
    const {
        video,
        forkliftList,
        onDetected,
        onUnrecognizedChange,
        detectionIntervalMs = DEFAULT_DETECTION_INTERVAL_MS,
        requiredConsecutiveMatches = DEFAULT_REQUIRED_CONSECUTIVE_MATCHES,
        maxProcessingWidth = DEFAULT_MAX_PROCESSING_WIDTH,
        maxHammingDistance,
    } = options;

    const detector = await createForkliftTagDetector(maxHammingDistance);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    let stopped = false;
    let rafId: number | null = null;
    let lastProcessed = 0;
    let candidate = { id: -1, count: 0 };
    let unrecognized = false;

    const setUnrecognized = (value: boolean) => {
        if (value !== unrecognized) {
            unrecognized = value;
            onUnrecognizedChange?.(value);
        }
    };

    const stop = () => {
        stopped = true;
        if (rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    };

    const processFrame = () => {
        if (stopped) {
            return;
        }
        rafId = requestAnimationFrame(processFrame);

        const now = performance.now();
        if (now - lastProcessed < detectionIntervalMs) {
            return;
        }
        lastProcessed = now;

        if (!ctx || video.readyState < 2) {
            return;
        }

        const scale = Math.min(1, maxProcessingWidth / video.videoWidth || 1);
        const width = Math.round(video.videoWidth * scale);
        const height = Math.round(video.videoHeight * scale);
        if (width === 0 || height === 0) {
            return;
        }
        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
        }

        ctx.drawImage(video, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);

        let markers: AprilTagMarker[] = [];
        try {
            markers = detector.detect(imageData);
        } catch {
            return;
        }

        const match = findMatchingForklift(markers, forkliftList);
        if (!match) {
            candidate = { id: -1, count: 0 };
            // A tag was decoded but its id is not in the forklift list.
            setUnrecognized(markers.length > 0);
            return;
        }
        setUnrecognized(false);

        const matchId = parseInt(match.tagValue, 10);
        if (candidate.id === matchId) {
            candidate.count += 1;
        } else {
            candidate = { id: matchId, count: 1 };
        }

        if (candidate.count >= requiredConsecutiveMatches) {
            stop();
            onDetected(match);
        }
    };

    if (stopped) {
        return { stop };
    }
    rafId = requestAnimationFrame(processFrame);
    return { stop };
}
